import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { downloadAuthed } from "@/lib/api";
import { CheckCircle2, CircleDashed, ChevronRight, Building2, IdCard, FileSignature, ClipboardList, FileText, ExternalLink, Copy, Loader2, AlertCircle, Info, Smartphone, QrCode, Banknote, Upload, Trash2, Ban, Gift } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import QuestionnaireForm from "@/components/client-portal/QuestionnaireForm";
import GiftorsManager from "@/components/app/onboarding/GiftorsManager";
import CclBody from "@/components/app/ccl/CclBody";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STEPS = [
  { key: "payment",       label: "Payment on account",   icon: Building2 },
  { key: "identity",      label: "ID verification",       icon: IdCard },
  { key: "ccl",           label: "Client Care Letter",    icon: FileSignature },
  { key: "questionnaires",label: "Client questionnaires", icon: ClipboardList },
  { key: "ta",            label: "TA form",               icon: FileText },
  { key: "funds",         label: "Proof of funds",        icon: Banknote },
  { key: "giftors",       label: "Gifted deposit",        icon: Gift },
];

export default function OnboardingWizardPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [current, setCurrent] = useState("payment");
  const [state, setState] = useState({ payment: null, funds: null, identity: null, ccl: null, qs: null, ta: null, taPdf: null, taNative: null, giftors: [] });
  const [disabledSteps, setDisabledSteps] = useState([]);
  const [cancelled, setCancelled] = useState(false);
  const [loading, setLoading] = useState(true);
  const didInitialAdvance = useRef(false);

  const visibleSteps = STEPS.filter(s => !disabledSteps.includes(s.key));

  // The wizard is a client-only flow — solicitors don't use it; if
  // they land here (e.g. by pasting a URL) send them to the case file.
  useEffect(() => {
    if (user && user.role !== "client") {
      navigate(`/cases/${caseId}`, { replace: true });
    }
  }, [user, caseId, navigate]);

  const fetchAll = useCallback(async () => {
    try {
      const [pay, ident, ccl, qs, ta, taPdf, taNative, funds, giftors, stepsCfg] = await Promise.all([
        api.get(`/onboarding/cases/${caseId}/payment-details`).then(r => r.data).catch(() => null),
        api.get(`/onboarding/cases/${caseId}/didit/summary`).then(r => r.data).catch(() => null),
        api.get(`/onboarding/cases/${caseId}/ccl`).then(r => r.data).catch(() => null),
        api.get(`/cases/${caseId}/questionnaires`).then(r => r.data).catch(() => null),
        api.get(`/onboarding/cases/${caseId}/ta`).then(r => r.data).catch(() => null),
        api.get(`/onboarding/cases/${caseId}/ta/pdf/status`).then(r => r.data).catch(() => null),
        api.get(`/onboarding/cases/${caseId}/ta-native/status`).then(r => r.data).catch(() => null),
        api.get(`/onboarding/cases/${caseId}/funds`).then(r => r.data).catch(() => null),
        api.get(`/onboarding/cases/${caseId}/giftors`).then(r => r.data?.giftors || []).catch(() => []),
        api.get(`/cases/${caseId}/onboarding-pack/steps`).then(r => r.data).catch(() => null),
      ]);
      setState({ payment: pay, identity: ident, ccl, qs, ta, taPdf, taNative, funds, giftors });
      setCancelled(!!stepsCfg?.onboarding_cancelled);
      const disabled = stepsCfg?.disabled_steps || [];
      setDisabledSteps(disabled);
      // Auto-advance to the first incomplete step, but ONLY on the
      // very first load. Subsequent refreshes (after e.g. signing a
      // TA form) must keep the user on the step they were on so they
      // can see the newly-generated download link without being
      // yanked back to Payment.
      if (!didInitialAdvance.current) {
        const doneByKey = {
          payment: !!pay?.declaration || pay?.worldpay?.latest_payment?.status === "paid",
          funds: !!funds?.required_met,
          identity: !!ident?.all_cleared,
          ccl: !!ccl?.all_signed,
          questionnaires: !!qs?.applicable?.length && qs.applicable.every(x => x.status === "all_submitted" || x.status === "submitted"),
          ta: (taNative?.enabled && taNative.forms.every(f => f.all_signed))
              || (taPdf?.enabled && taPdf.all_signed)
              || (!taPdf?.enabled && !!ta?.all_signed),
          giftors: true,
        };
        // First incomplete step among the steps NOT removed for this case.
        const order = ["payment", "identity", "ccl", "questionnaires", "ta", "funds", "giftors"];
        const firstIncomplete = order.find(k => !disabled.includes(k) && !doneByKey[k]);
        const firstVisible = order.find(k => !disabled.includes(k));
        setCurrent(firstIncomplete || firstVisible || "payment");
        didInitialAdvance.current = true;
      }
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const stepStatus = (key) => {
    if (key === "payment") return state.payment?.declaration || state.payment?.worldpay?.latest_payment?.status === "paid" ? "done" : "todo";
    if (key === "funds") return state.funds?.required_met ? "done" : "todo";
    if (key === "identity") return state.identity?.all_cleared ? "done" : "todo";
    if (key === "ccl") return state.ccl?.all_signed ? "done" : "todo";
    if (key === "questionnaires") {
      const items = state.qs?.applicable || [];
      if (!items.length) return "todo";
      return items.every(x => x.status === "all_submitted" || x.status === "submitted") ? "done" : "todo";
    }
    if (key === "ta") {
      // Prefer native-schema flow when applicable (TA10 etc.) — this
      // is the LEAP-style rebuild that replaces the raw-AcroForm UI.
      if (state.taNative?.enabled) {
        const forms = state.taNative.forms || [];
        return forms.length && forms.every(f => f.all_signed) ? "done" : "todo";
      }
      // If the firm has uploaded PDF templates, that flow drives status.
      if (state.taPdf?.enabled) return state.taPdf?.all_signed ? "done" : "todo";
      return state.ta?.all_signed ? "done" : "todo";
    }
    if (key === "giftors") return "done";  // optional/non-blocking
    return "todo";
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-3"><Loader2 className="w-8 h-8 text-slate-400 animate-spin" /><p className="text-sm text-slate-500">Loading your onboarding…</p></div>
    </div>;
  }

  if (cancelled) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="wizard-cancelled-screen">
      <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mb-4">
          <Ban className="w-6 h-6 text-rose-500" />
        </div>
        <h1 className="text-lg font-semibold text-slate-900 mb-2">This onboarding is no longer active</h1>
        <p className="text-sm text-slate-600">Your solicitor has cancelled this onboarding link. If you were expecting to complete your onboarding, please contact your solicitor — they'll send you a fresh link.</p>
        <button onClick={() => navigate("/")} className="mt-6 text-sm px-4 py-2 rounded-md bg-[#100f5d] text-white hover:bg-[#1a1980]" data-testid="wizard-cancelled-back">Back to portal</button>
      </div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="onboarding-wizard-page">
      <header className="sticky top-0 z-40 bg-[#100f5d] text-white shadow-sm">
        <div className="h-[3px] bg-[#c87d3a]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="UKC Legal" className="h-9 w-auto" />
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/10 text-[#e89e63]">Onboarding</span>
          </div>
          <button onClick={() => navigate("/")} className="text-xs text-slate-300 hover:text-white" data-testid="wizard-back-btn">Back to portal</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left rail — step tracker */}
        <nav className="lg:col-span-4">
          {(() => {
            const allDone = visibleSteps.every(s => stepStatus(s.key) === "done");
            return allDone ? (
              <button type="button" data-testid="wizard-pack-download-btn"
                 onClick={() => downloadAuthed(`/onboarding/cases/${caseId}/pack`, "onboarding-pack.zip")
                   .catch(() => toast.error("Could not download the pack. Please try again."))}
                 className="mb-4 w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm text-left">
                <CheckCircle2 className="w-5 h-5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">Download your onboarding pack</p>
                  <p className="text-[11px] opacity-80">Everything you've signed, zipped up.</p>
                </div>
              </button>
            ) : null;
          })()}
          <ol className="space-y-2" data-testid="wizard-steps">
            {visibleSteps.map((s, i) => {
              const isDone = stepStatus(s.key) === "done";
              const isCurrent = current === s.key;
              const Icon = s.icon;
              return (
                <li key={s.key}>
                  <button
                    type="button"
                    onClick={() => setCurrent(s.key)}
                    data-testid={`wizard-step-${s.key}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all ${
                      isCurrent ? "border-[#100f5d] bg-white shadow-sm" :
                      isDone   ? "border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50" :
                                 "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      isDone ? "bg-emerald-600 text-white" : isCurrent ? "bg-[#100f5d] text-white" : "bg-slate-100 text-slate-400"
                    }`}>
                      {isDone ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs uppercase tracking-wider text-slate-400">Step {i + 1}</p>
                      <p className={`text-sm font-medium ${isCurrent ? "text-[#100f5d]" : isDone ? "text-emerald-700" : "text-slate-700"}`}>{s.label}</p>
                    </div>
                    {isCurrent && <ChevronRight className="w-4 h-4 text-[#100f5d]" />}
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Right — active step panel */}
        <section className="lg:col-span-8">
          {current === "payment"        && <PaymentStep caseId={caseId} data={state.payment} refresh={fetchAll} />}
          {current === "funds"          && <FundsStep caseId={caseId} data={state.funds} refresh={fetchAll} />}
          {current === "identity"       && <IdentityStep caseId={caseId} data={state.identity} refresh={fetchAll} isClient={user?.role === "client"} />}
          {current === "ccl"            && <CclStep caseId={caseId} data={state.ccl} refresh={fetchAll} />}
          {current === "questionnaires" && <QuestionnairesStep caseId={caseId} data={state.qs} refresh={fetchAll} />}
          {current === "ta"             && <TaStep caseId={caseId} data={state.ta} pdfData={state.taPdf} nativeData={state.taNative} refresh={fetchAll} />}
          {current === "giftors"        && <GiftorsStep caseId={caseId} data={state.giftors} refresh={fetchAll} />}
        </section>
      </main>
    </div>
  );
}

// ─────────────────────────── Step 1 · Payment ────────────────────────
function PaymentStep({ caseId, data, refresh }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  if (!data) return <Card><p className="text-sm text-slate-500">Loading payment details…</p></Card>;

  const bank = data.bank_details || {};
  const alreadyDeclared = !!data.declaration;
  const paidByCard = data.worldpay?.latest_payment?.status === "paid";
  const wpEnabled = !!data.worldpay?.enabled;
  const consent = data.communication_consent || null;
  const consented = !!consent?.consented;
  const consentText = data.communication_consent_text || "";

  const giveConsent = async () => {
    setConsentBusy(true);
    try {
      await api.post(`/onboarding/communication-consent`, { case_id: caseId });
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record your acknowledgement.");
    } finally { setConsentBusy(false); }
  };

  const declareTransfer = async () => {
    setBusy(true);
    try {
      await api.post(`/onboarding/payment-declaration`, { case_id: caseId, notes });
      toast.success("Transfer declared. Your solicitor will reconcile against the client account.");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record your declaration.");
    } finally { setBusy(false); }
  };

  const payByCard = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/onboarding/cases/${caseId}/worldpay/checkout`);
      window.open(r.data.redirect_url, "_blank", "noopener");
      toast.success("Payment window opened in a new tab.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment provider not available.");
    } finally { setBusy(false); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  return (
    <Card testid="wizard-payment">
      <StepTitle icon={Building2} title="Payment on account" done={alreadyDeclared || paidByCard} />
      {(alreadyDeclared || paidByCard) && (
        <Banner tone="success" data-testid="wizard-payment-done">
          {paidByCard ? "Payment received by card — thank you." : "Transfer declared. Your solicitor will match it against our client account."}
        </Banner>
      )}

      <p className="text-sm text-slate-600 mt-4">
        Please pay <strong>£{Number(data.amount || 0).toFixed(2)}</strong> onto our client account
        {" "}using the reference <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{data.reference}</span>.
      </p>

      {/* Mandatory communication / service-model consent */}
      <div className={`mt-6 rounded-lg border p-4 ${consented ? "border-emerald-200 bg-emerald-50/50" : "border-amber-300 bg-amber-50/60"}`}
        data-testid="wizard-communication-consent">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          {consented ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <CircleDashed className="w-4 h-4 text-amber-500" />}
          Communication &amp; service-model consent
        </h3>
        <label className="mt-3 flex gap-3 items-start cursor-pointer">
          <input
            type="checkbox"
            checked={consented}
            disabled={consented || consentBusy}
            onChange={() => { if (!consented) giveConsent(); }}
            data-testid="wizard-consent-checkbox"
            className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-[#100f5d] focus:ring-[#100f5d] disabled:opacity-70" />
          <span className="text-[13px] leading-relaxed text-slate-700">{consentText}</span>
        </label>
        {consented ? (
          <p className="mt-2 text-xs text-emerald-700" data-testid="wizard-consent-timestamp">
            Acknowledged{consent?.consented_by_name ? ` by ${consent.consented_by_name}` : ""} on{" "}
            {new Date(consent.consented_at).toLocaleString("en-GB")}.
          </p>
        ) : (
          <p className="mt-2 text-xs text-amber-700">Please tick this box to continue with your payment.</p>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        {/* Card option */}
        <div className={`rounded-lg border-2 p-5 ${wpEnabled ? "border-[#100f5d]" : "border-slate-200 bg-slate-50/50"}`}>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">Pay by card {!wpEnabled && <span className="text-[10px] font-semibold uppercase bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">Coming soon</span>}</h3>
          <p className="text-xs text-slate-500 mt-1">Secure card payment via WorldPay. Opens in a new tab.</p>
          <button disabled={!wpEnabled || busy || paidByCard || !consented} onClick={payByCard} data-testid="wizard-pay-card-btn"
            className="mt-4 w-full px-4 py-2.5 rounded-lg text-sm font-semibold bg-[#100f5d] text-white disabled:bg-slate-300 disabled:cursor-not-allowed">
            {paidByCard ? "Paid" : busy ? "Opening…" : "Pay £" + Number(data.amount || 0).toFixed(2)}
          </button>
        </div>

        {/* Bank transfer option */}
        <div className="rounded-lg border-2 border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900">Bank transfer</h3>
          <dl className="mt-3 space-y-1.5 text-sm">
            {[["Account name", bank.account_name], ["Sort code", bank.sort_code], ["Account number", bank.account_number], ["Bank", bank.bank_name], ["Reference", data.reference]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-2">
                <dt className="text-slate-500">{k}</dt>
                <dd className="font-mono text-slate-900 flex items-center gap-1">
                  {v || "—"}
                  {v && <button onClick={() => copy(v)} className="text-slate-400 hover:text-slate-700" data-testid={`wizard-copy-${k.replace(/\s+/g,'-').toLowerCase()}`}><Copy className="w-3.5 h-3.5" /></button>}
                </dd>
              </div>
            ))}
          </dl>
          {!alreadyDeclared && (<>
            <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Optional notes (e.g. sent from Halifax 12345678)"
              className="mt-3 w-full text-sm border border-slate-200 rounded p-2"
              data-testid="wizard-transfer-notes" />
            <button onClick={declareTransfer} disabled={busy || !consented} data-testid="wizard-declare-transfer-btn"
              className="mt-3 w-full px-4 py-2.5 rounded-lg text-sm font-semibold border-2 border-[#100f5d] text-[#100f5d] hover:bg-[#100f5d] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? "Recording…" : "I've sent the transfer"}
            </button>
          </>)}
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────── Step · Proof of funds ──────────────────────
// The client uploads evidence of where their money comes from. Proof of
// funds (6 months' bank statements, savings, gift letters) is REQUIRED;
// the mortgage offer is optional (cash buyers have none). Files are
// filed straight into the case's "Proof of Funds" documents folder.
function FundsUploadZone({ caseId, kind, title, hint, required, files, refresh }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const doUpload = async (fileList) => {
    const items = Array.from(fileList || []);
    if (!items.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of items) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", kind);
      try {
        await api.post(`/onboarding/cases/${caseId}/funds`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) {
        toast.error(e.response?.data?.detail || `Could not upload ${f.name}`);
      }
    }
    if (ok) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
    if (inputRef.current) inputRef.current.value = "";
    setBusy(false);
    await refresh();
  };

  const remove = async (docId) => {
    try {
      await api.delete(`/onboarding/cases/${caseId}/funds/${docId}`);
      toast.success("File removed");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove the file");
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-4" data-testid={`funds-zone-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          {title}
          {required
            ? <span className="text-[10px] font-semibold uppercase bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">Required</span>
            : <span className="text-[10px] font-semibold uppercase bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Optional</span>}
        </h3>
        {files.length > 0 && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
      </div>
      <p className="text-xs text-slate-500 mt-1">{hint}</p>

      {files.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid={`funds-list-${kind}`}>
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50/60 px-3 py-2">
              <button type="button"
                onClick={() => downloadAuthed(`/documents/${f.id}/download`, f.filename)
                  .catch(() => toast.error("Could not download this file."))}
                className="text-sm text-[#100f5d] hover:underline flex items-center gap-2 min-w-0 text-left" data-testid={`funds-file-${f.id}`}>
                <FileText className="w-4 h-4 shrink-0" />
                <span className="truncate">{f.filename}</span>
              </button>
              <button onClick={() => remove(f.id)} className="text-slate-400 hover:text-rose-600 shrink-0" title="Remove" data-testid={`funds-remove-${f.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        className="hidden" data-testid={`funds-input-${kind}`}
        onChange={(e) => doUpload(e.target.files)} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
        data-testid={`funds-upload-btn-${kind}`}
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border-2 border-[#100f5d] text-[#100f5d] hover:bg-[#100f5d] hover:text-white transition-colors disabled:opacity-50">
        <Upload className="w-4 h-4" /> {busy ? "Uploading…" : files.length ? "Add more files" : "Upload files"}
      </button>
    </div>
  );
}

function FundsStep({ caseId, data, refresh }) {
  if (!data) return <Card><p className="text-sm text-slate-500">Loading…</p></Card>;
  const proof = data.proof_of_funds || [];
  const mortgage = data.mortgage_offer || [];
  const done = !!data.required_met;
  return (
    <Card testid="wizard-funds">
      <StepTitle icon={Banknote} title="Proof of funds" done={done} />
      <p className="text-sm text-slate-600 mt-4">
        We're required to verify the source of the money used in your transaction. Please upload clear copies
        (PDF, JPG or PNG, up to 15MB each). You can upload several files.
      </p>
      {done
        ? <Banner tone="success" data-testid="wizard-funds-done">Thank you — your proof of funds has been received. You can add more files at any time.</Banner>
        : <Banner tone="warning" data-testid="wizard-funds-todo">Please upload your proof of funds to continue. This step is required.</Banner>}

      <div className="mt-6 space-y-4">
        <FundsUploadZone
          caseId={caseId} kind="proof_of_funds" required
          title="Proof of funds"
          hint="Your last 6 months' bank statements showing the funds, plus any savings account statements or gift letters (with the giftor's details)."
          files={proof} refresh={refresh} />
        <FundsUploadZone
          caseId={caseId} kind="mortgage_offer" required={false}
          title="Mortgage offer"
          hint="If you're buying with a mortgage, upload your lender's formal mortgage offer. Cash buyers can leave this blank."
          files={mortgage} refresh={refresh} />
      </div>
    </Card>
  );
}

// ─────────────────── Step · Gifted deposit (giftors) ─────────────────
// Optional/non-blocking. The buyer declares anyone gifting money towards
// the deposit; each giftor is emailed a secure link to sign a declaration,
// upload proof of funds and complete ID/AML. Managed here + in the
// solicitor's onboarding panel via the shared GiftorsManager.
function GiftorsStep({ caseId, data, refresh }) {
  const giftors = data || [];
  return (
    <Card testid="wizard-giftors">
      <StepTitle icon={Gift} title="Gifted deposit" done={true} />
      <p className="text-sm text-slate-600 mt-4">
        Is anyone gifting you money towards your deposit or purchase (for example a parent or grandparent)?
        If so, add them below. We'll email each giftor a secure link to sign a short gifted-deposit declaration,
        upload proof of their funds, and confirm their identity. This step is optional — leave it blank if it
        doesn't apply.
      </p>
      <Banner tone="info" data-testid="wizard-giftors-info">Giftors complete their own steps separately — you don't need to wait for them to finish your onboarding.</Banner>
      <div className="mt-6">
        <GiftorsManager caseId={caseId} role="client" giftors={giftors} refresh={refresh} />
      </div>
    </Card>
  );
}



// ─────────────────────────── Step 2 · Identity ───────────────────────
// Uses Didit's "Mobile Hand-Off" pattern (Safe-Harbour requirement —
// NFC passport-chip reads only work on physical mobile devices, not in
// desktop browsers). On desktop: we show a QR code the client scans
// with their phone → Didit's hosted mobile page performs the NFC read →
// desktop refreshes automatically via 5s poll. On mobile: we do a
// full top-level window redirect so Didit owns the tab and can trigger
// the native iOS/Android NFC modal (iframes block NFC access).
function IdentityStep({ caseId, data, refresh, isClient }) {
  const [busy, setBusy] = useState(null);
  const [handoff, setHandoff] = useState(null); // { clientId, sessionUrl, sessionId }
  const [overrideRow, setOverrideRow] = useState(null); // client row pending OCR-only confirm
  const [overriding, setOverriding] = useState(false);

  const confirmOverride = async () => {
    if (!overrideRow) return;
    setOverriding(true);
    try {
      await api.post(`/onboarding/cases/${caseId}/didit/override`);
      toast.success("Noted — you can continue. Your solicitor may still ask for the chip scan before completion.");
      setOverrideRow(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record your choice. Please try again.");
    } finally {
      setOverriding(false);
    }
  };

  useEffect(() => {
    // Poll every 5s for status changes while on this step. Speed up to
    // every 2s while the mobile hand-off QR panel is open so the
    // desktop tab picks up the "chip read done" webhook faster.
    const interval = handoff ? 2000 : 5000;
    const id = setInterval(refresh, interval);
    return () => clearInterval(id);
  }, [refresh, handoff]);

  // Also refresh whenever the tab regains focus — solves the case
  // where the user finishes on mobile and jumps back to the desktop tab.
  useEffect(() => {
    const onVis = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refresh]);

  const isMobileDevice = () => {
    if (typeof window === "undefined") return false;
    // Coarse UA detection is fine here — worst case a tablet gets the
    // QR flow which still works. iPadOS reports "MacIntel" so we also
    // check maxTouchPoints > 1 to catch modern iPads.
    const ua = navigator.userAgent || "";
    const mobile = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
    const touchpadOs = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
    return mobile || touchpadOs;
  };

  // Auto-close the QR panel once the ID for the current client
  // becomes approved (Didit webhook → poll → status flips). Also
  // triggers a bright success toast + browser Notification (if the
  // desktop tab is in the background) so the client doesn't miss
  // that they can now continue on the computer.
  useEffect(() => {
    if (!handoff || !data?.clients) return;
    const row = data.clients.find(c => c.client_id === handoff.clientId);
    if (row?.approved) {
      const nfc = row.safe_harbour_ready;
      toast.success(
        nfc ? "ID + NFC chip verified — you can carry on here." :
              "ID verified — you can carry on here.",
        { duration: 6000 },
      );
      // Browser Notification — best-effort, requires the user to have
      // granted permission (we ask on first QR show).
      try {
        if (document.hidden && "Notification" in window && Notification.permission === "granted") {
          new Notification("Convey Hub — ID verified", {
            body: nfc ? "Passport chip read successfully. Return to your computer to continue."
                      : "Identity check complete. Return to your computer to continue.",
            tag:  "conveyhub-id-done",
          });
        }
      } catch { /* older browsers — silently ignore */ }
      setHandoff(null);
    }
  }, [data, handoff]);

  // Ask for Notification permission the first time the QR panel opens
  // — the user has to interact with the wizard to open it so this is
  // never surprise-prompt.
  useEffect(() => {
    if (!handoff) return;
    try {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
    } catch { /* Safari private browsing throws */ }
  }, [handoff]);

  if (!data) return <Card><p className="text-sm text-slate-500">Loading identity check status…</p></Card>;

  const start = async (clientId, isMe, forceNew = false) => {
    setBusy(clientId);
    try {
      const params = [];
      if (!(isMe || !clientId)) params.push(`client_id=${encodeURIComponent(clientId)}`);
      if (forceNew) params.push("force_new=true");
      const q = params.length ? `?${params.join("&")}` : "";
      const r = await api.post(`/onboarding/cases/${caseId}/didit/session${q}`);
      const sessionUrl = r.data.url;

      // ── If a native Convey Hub shell is present, delegate straight
      //    to it — that's the future path once Didit's native SDK is
      //    wired up in the Capacitor app.
      if (window.__conveyHubBridge?.startIdCheck) {
        await window.__conveyHubBridge.startIdCheck(sessionUrl);
        toast.success("Identity check opened. Return here after you finish.");
        await refresh();
        return;
      }

      // ── Scenario B: already on mobile → full top-level redirect so
      //    Didit owns the tab context and can invoke native NFC.
      if (isMobileDevice()) {
        // Preserve the wizard URL as a return hint (Didit will 302 back
        // to whatever we set on the session's callback — for a mobile
        // top-level redirect the callback is respected as the final
        // landing page).
        window.location.href = sessionUrl;
        return;
      }

      // ── Scenario A: desktop → open the QR panel. Client scans it
      //    with their phone camera which opens Didit's hosted mobile
      //    interface where NFC is available. Desktop polls the same
      //    /didit/summary endpoint every 5s and picks up the result.
      setHandoff({
        clientId,
        sessionUrl,
        sessionId: r.data.session_id,
        clientName: (data.clients.find(x => x.client_id === clientId) || {}).name || "You",
      });
      toast.success("Scan the QR code with your phone to continue.");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start identity verification.");
    } finally { setBusy(null); }
  };
  return (
    <Card testid="wizard-identity">
      <StepTitle icon={IdCard} title="Identity verification" done={data.all_cleared} />
      <p className="text-sm text-slate-600 mt-2">
        HM Land Registry (Practice Guide 67) requires every party to verify their ID with an NFC chip read of a passport or national ID, plus an active liveness selfie and AML screen. Driving licences are not accepted.
      </p>

      {/* Client explainer — why the phone step is mandatory + how to
          enable NFC on iPhone / Android. Collapsed by default so
          confident clients aren't nagged; expanded for anyone who hits
          "Why do I need my phone?". */}
      <NfcExplainer />

      {/* Mobile hand-off panel — shows when a session has been started
          on desktop and the client needs to scan the QR with their phone. */}
      {handoff && (
        <MobileHandoffPanel handoff={handoff} onClose={() => setHandoff(null)} />
      )}
      <div className="mt-4 space-y-3">
        {data.clients.map(c => (
          <div key={c.client_id} className={`rounded-lg border p-4 ${c.approved ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`wizard-identity-row-${c.client_id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-slate-900 flex items-center gap-2">{c.name}{c.is_me && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">You</span>}</p>
                <p className="text-xs text-slate-500">{c.email}</p>
              </div>
              <div className="flex items-center gap-1.5">
                {c.approved && c.safe_harbour_ready && (
                  <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                    title="ePassport chip data (DG1 + DG2) verified — HMLR Safe Harbour compliant"
                    data-testid={`wizard-safe-harbour-${c.client_id}`}>
                    ✓ Safe Harbour
                  </span>
                )}
                {c.approved && !c.safe_harbour_ready && (
                  <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                    title="ID approved but NFC chip was not read — HMLR Safe Harbour requires a chip read"
                    data-testid={`wizard-no-safe-harbour-${c.client_id}`}>
                    OCR only
                  </span>
                )}
                <StatusPill status={c.wizard_status} />
              </div>
            </div>
            {!c.approved && (isClient ? c.is_me : true) && (
              c.wizard_status === "in_review" ? (
                <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50/60 p-3" data-testid={`wizard-identity-inreview-${c.client_id}`}>
                  <p className="text-xs text-blue-800 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Your documents have been received and your identity check is <strong>being reviewed</strong>. There's nothing more for you to do — please don't submit your passport or proof of address again. We'll be in touch if anything else is needed.</span>
                  </p>
                </div>
              ) : (!c.legal_name_confirmed && c.wizard_status === "not_started") ? (
                <LegalNameConfirm caseId={caseId} client={c} refresh={refresh} />
              ) : (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button onClick={() => start(c.client_id, c.is_me, c.wizard_status === "declined")} disabled={busy === c.client_id}
                  data-testid={`wizard-verify-btn-${c.client_id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#100f5d] text-white disabled:opacity-50">
                  {busy === c.client_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                  {c.wizard_status === "pending" ? "Resume verification"
                   : c.wizard_status === "declined" ? "Start new ID check"
                   : `Verify ${c.is_me ? "me" : c.name.split(" ")[0]}`}
                </button>
                {c.wizard_status === "declined" && (
                  <span className="text-xs text-rose-700">
                    Previous ID check was declined. Start a new one or contact your solicitor if you need help.
                  </span>
                )}
              </div>
              )
            )}
            {isClient && !c.is_me && !c.approved && (
              <p className="mt-2 text-xs text-slate-500 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> {c.name} will complete their own check from their sign-in link.</p>
            )}
            {/* OCR-only branch — approved but no NFC chip read. The client
                can retry the chip scan (preferred) OR choose to continue
                without it (warned + audited). No solicitor override. */}
            {c.approved && !c.safe_harbour_ready && !c.client_override && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid={`id-ocr-only-notice-${c.client_id}`}>
                <p className="text-xs text-amber-800 flex items-start gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>Your ID was approved by photo (OCR) only — the secure chip in your document wasn't read. A chip read is the strongest check and the fastest route to completion.</span>
                </p>
                {(isClient ? c.is_me : false) && (
                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <button onClick={() => start(c.client_id, c.is_me, true)} disabled={busy === c.client_id}
                      data-testid={`id-scan-chip-btn-${c.client_id}`}
                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold bg-[#100f5d] text-white disabled:opacity-50">
                      {busy === c.client_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
                      Scan the chip with my phone
                    </button>
                    <button onClick={() => setOverrideRow(c)}
                      data-testid={`id-client-override-open-${c.client_id}`}
                      className="text-xs font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900">
                      I can't scan the chip — continue without it
                    </button>
                  </div>
                )}
                {isClient && !c.is_me && (
                  <p className="mt-1.5 text-[11px] text-amber-700">{c.name} can scan their chip or choose to continue from their own sign-in link.</p>
                )}
              </div>
            )}
            {c.approved && !c.safe_harbour_ready && c.client_override && (
              <p className="mt-3 text-xs text-amber-800 bg-amber-50/60 border border-amber-200 rounded-lg p-2.5 flex items-start gap-1.5" data-testid={`id-override-banner-${c.client_id}`}>
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Continuing with photo/OCR verification only (no chip read). You can still scan the chip any time before completion.</span>
              </p>
            )}
          </div>
        ))}
      </div>
      {data.total > 1 && !data.all_cleared && (
        <p className="mt-4 text-xs text-slate-500 border-t border-slate-100 pt-3">
          <strong>{data.approved} of {data.total}</strong> clients verified. Step 3 unlocks once everyone is done.
        </p>
      )}

      <AlertDialog open={!!overrideRow} onOpenChange={(o) => !o && setOverrideRow(null)}>
        <AlertDialogContent data-testid="id-client-override-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Continue without a chip scan?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-600 space-y-2">
                <p>Scanning your document's chip is the quickest, most secure way to verify your identity. If you continue without it:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>your ID is verified by photo/OCR only, which is a weaker check;</li>
                  <li>your solicitor may have to request additional documents; and</li>
                  <li>this can delay your completion.</li>
                </ul>
                <p>You can still go back and scan the chip at any time before completion.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={overriding} data-testid="id-client-override-cancel">Go back and try the chip</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmOverride(); }} disabled={overriding}
              className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600" data-testid="id-client-override-confirm">
              {overriding ? "Saving…" : "Continue without chip"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ──────────────── Legal-name confirmation (client, pre-ID) ────────────
// Shown on the ID step BEFORE the first Didit session so the client
// confirms their full legal name exactly as printed on their passport
// (incl. all middle names). This is passed to Didit as expected_details
// so its data-review pre-matches and never flags a name "re-split" —
// the cause of the false-positive "middle name mismatch" review.
function LegalNameConfirm({ caseId, client, refresh }) {
  const splitName = (full) => {
    const parts = (full || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { first: "", middle: "", surname: "" };
    if (parts.length === 1) return { first: parts[0], middle: "", surname: "" };
    if (parts.length === 2) return { first: parts[0], middle: "", surname: parts[1] };
    return { first: parts[0], middle: parts.slice(1, -1).join(" "), surname: parts[parts.length - 1] };
  };
  const fallback = splitName(client.name);
  const [first, setFirst] = useState(client.first_name || fallback.first);
  const [middle, setMiddle] = useState(client.middle_name || fallback.middle);
  const [surname, setSurname] = useState(client.surname || fallback.surname);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!first.trim() || !surname.trim()) {
      toast.error("Please enter both your first name and surname as printed on your passport.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/onboarding/cases/${caseId}/legal-name`, {
        first_name: first.trim(), middle_name: middle.trim(), surname: surname.trim(),
      });
      toast.success("Name confirmed — you can now verify your identity.");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save your name. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-3 rounded-lg border border-[#100f5d]/30 bg-[#100f5d]/5 p-4" data-testid={`wizard-legal-name-${client.client_id}`}>
      <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
        <IdCard className="w-4 h-4 text-[#100f5d]" /> Confirm your full legal name
      </p>
      <p className="text-xs text-slate-600 mt-1">
        Please enter your name <strong>exactly as it is printed on your passport</strong>, including <strong>every middle name</strong>. This must match your document or your ID check can be delayed.
      </p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-[11px] font-medium text-slate-500">First name</span>
          <input value={first} onChange={(e) => setFirst(e.target.value)} data-testid={`legal-name-first-${client.client_id}`}
            className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2.5 py-2" placeholder="e.g. Louise" />
        </label>
        <label className="block">
          <span className="text-[11px] font-medium text-slate-500">Middle name(s) <span className="text-slate-400">— if any</span></span>
          <input value={middle} onChange={(e) => setMiddle(e.target.value)} data-testid={`legal-name-middle-${client.client_id}`}
            className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2.5 py-2" placeholder="e.g. Anne" />
        </label>
        <label className="block">
          <span className="text-[11px] font-medium text-slate-500">Surname</span>
          <input value={surname} onChange={(e) => setSurname(e.target.value)} data-testid={`legal-name-surname-${client.client_id}`}
            className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2.5 py-2" placeholder="e.g. McConnell" />
        </label>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">
        No middle name on your passport? Leave the middle box empty and continue.
      </p>
      <button onClick={save} disabled={saving} data-testid={`legal-name-save-${client.client_id}`}
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#100f5d] text-white disabled:opacity-50">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
        {saving ? "Saving…" : "Confirm name & continue"}
      </button>
    </div>
  );
}


// ──────────────────── Mobile hand-off (QR code) panel ────────────────
// Rendered from IdentityStep after `start()` on a desktop. Shows a
// QR code containing the Didit session URL — client scans it with
// their phone, opens Didit's mobile page, does the NFC read there.
// The desktop wizard's 5s poll picks up the webhook result and this
// panel auto-hides. `handoff` = { sessionUrl, clientName, ... }.
function MobileHandoffPanel({ handoff, onClose }) {
  const { sessionUrl, clientName } = handoff;
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(sessionUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fall back to a manual selection prompt */ }
  };
  return (
    <div className="mt-5 rounded-xl border-2 border-[#100f5d] bg-white p-5"
         data-testid="wizard-identity-handoff">
      <div className="flex items-center gap-3 mb-3">
        <Smartphone className="w-6 h-6 text-[#100f5d]" />
        <div className="flex-1">
          <p className="text-base font-semibold text-slate-900">Continue on your phone</p>
          <p className="text-xs text-slate-500">
            HM Land Registry requires a secure chip scan of your passport.
            This can only be done on a phone.
          </p>
        </div>
        <button type="button" onClick={onClose}
          className="text-xs text-slate-400 hover:text-slate-700 px-2 py-1"
          data-testid="wizard-identity-handoff-close">
          Cancel
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
        <div className="justify-self-center bg-white p-3 rounded-lg border border-slate-200">
          <QRCodeSVG value={sessionUrl} size={180} level="M" includeMargin={false}
            data-testid="wizard-identity-qr" />
        </div>
        <div>
          <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
            <li>Open your phone's <b>camera app</b>.</li>
            <li>Point it at this QR code — tap the link that appears.</li>
            <li>Follow the on-screen prompts: <b>take a photo of your passport</b> then <b>hold the passport to the back of your phone</b> to read the chip.</li>
            <li>Return to this page — it will update automatically when you finish.</li>
          </ol>
          <div className="mt-4 flex items-center gap-2">
            <button type="button" onClick={copyLink}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
              data-testid="wizard-identity-handoff-copy">
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Or copy the link"}
            </button>
            <a href={sessionUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
              data-testid="wizard-identity-handoff-open">
              <ExternalLink className="w-3.5 h-3.5" />
              Open on this device
            </a>
          </div>
          <p className="mt-3 text-[11px] text-slate-500 flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" />
            Waiting for {clientName}'s phone to finish…
          </p>
        </div>
      </div>
    </div>
  );
}


// ─────────────────── NFC explainer (client-facing) ───────────────────
// Collapsible helper panel on the wizard ID step. Explains the two
// things clients ALWAYS want to know before scanning a QR code with
// their phone:
//   (1) Why do I have to use my phone?
//   (2) How do I enable NFC on my phone?
// Kept collapsed by default so confident clients aren't nagged.
function NfcExplainer() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white overflow-hidden"
         data-testid="wizard-identity-explainer">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-slate-50"
        data-testid="wizard-identity-explainer-toggle">
        <span className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <Info className="w-4 h-4 text-[#100f5d]" />
          Why do I need my phone? &nbsp;How do I turn NFC on?
        </span>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/60 space-y-3 text-sm text-slate-700">
          <div>
            <p className="font-semibold text-slate-900 mb-1">Why the phone step is mandatory</p>
            <p>
              HM Land Registry (Practice Guide&nbsp;8) requires solicitors to <b>read the encrypted chip</b> in your passport, not just take a photo of the front page. Web browsers on computers can't do this — <b>only phones with NFC can</b>. So we hand you off to your phone for 60 seconds.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-md border border-slate-200 bg-white p-3">
              <p className="font-semibold text-slate-900 mb-1 flex items-center gap-1.5">
                <Smartphone className="w-4 h-4" /> iPhone
              </p>
              <ol className="list-decimal list-inside space-y-0.5 text-[13px]">
                <li>Any iPhone 7 or newer works (2016+).</li>
                <li>NFC is <b>always on</b> — no setting to change.</li>
                <li>When the app asks, hold the <b>top edge</b> of your iPhone flat against the photo page of your passport.</li>
                <li>Keep it still for ~10 seconds until you feel a buzz.</li>
              </ol>
            </div>
            <div className="rounded-md border border-slate-200 bg-white p-3">
              <p className="font-semibold text-slate-900 mb-1 flex items-center gap-1.5">
                <Smartphone className="w-4 h-4" /> Android
              </p>
              <ol className="list-decimal list-inside space-y-0.5 text-[13px]">
                <li>Any Android 6+ phone with NFC works.</li>
                <li>Enable NFC once: <b>Settings → Connected devices → NFC</b> (or search "NFC" in Settings).</li>
                <li>When prompted, hold the <b>back centre</b> of your phone flat against the photo page.</li>
                <li>Keep it still for ~10 seconds.</li>
              </ol>
            </div>
          </div>
          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[12px] text-blue-900">
            <b>Tip:</b> remove your phone case if the read fails — thick cases block the signal.
            The chip lives in the little rectangle icon on the front of the passport near the middle.
          </div>
        </div>
      )}
    </div>
  );
}



// ─────────────────────────── Step 3 · CCL ────────────────────────────
function CclStep({ caseId, data, refresh }) {
  const [activeForm, setActiveForm] = useState("ccl");
  if (!data) return <Card><p className="text-sm text-slate-500">Loading Client Care Letter…</p></Card>;
  // Legacy response shape: single `signers` + `body`. Wrap it as one CCL form
  // so this component keeps working if the API rolls back.
  const forms = data.forms || [{
    form: "ccl", label: "Client Care Letter", body: data.body,
    signers: data.signers, all_signed: data.all_signed,
    final_document_id: data.final_document_id, uses_bound_template: false,
  }];
  const active = forms.find(f => f.form === activeForm) || forms[0];
  const me = (active.signers || []).find(s => s.is_me) || (active.signers || [])[0];
  const stepDone = forms.every(f => f.all_signed);

  return (
    <Card testid="wizard-ccl">
      <StepTitle icon={FileSignature} title="Client Care Letter & Terms of Business" done={stepDone} />
      <p className="text-sm text-slate-600 mt-2">
        Please review each document, then sign to confirm you accept it. Both must be signed by every client on the case.
      </p>

      {/* Form tabs */}
      <div className="mt-4 flex flex-wrap gap-2 border-b border-slate-200">
        {forms.map(f => {
          const isActive = activeForm === f.form;
          const iSigned = !!(f.signers || []).find(s => s.is_me)?.signed;
          return (
            <button key={f.form} type="button" onClick={() => setActiveForm(f.form)}
              data-testid={`wizard-ccl-tab-${f.form}`}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                isActive ? "border-[#100f5d] text-[#100f5d]"
                         : "border-transparent text-slate-500 hover:text-slate-900"
              }`}>
              {f.all_signed ? <CheckCircle2 className="w-4 h-4 inline mr-1 text-emerald-600" /> :
               iSigned      ? <CheckCircle2 className="w-4 h-4 inline mr-1 text-amber-500" /> :
                              <CircleDashed className="w-4 h-4 inline mr-1 text-slate-300" />}
              {f.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4 max-h-80 overflow-y-auto rounded border border-slate-200 bg-white p-4 text-[13px] leading-relaxed font-serif"
        data-testid={`wizard-ccl-body-${active.form}`}>
        {active.body ? <CclBody text={active.body} /> : "—"}
      </div>
      {active.uses_bound_template === false && (
        <p className="mt-1 text-[11px] text-slate-400">Using the built-in template. Your firm can bind a custom Word template in Doc Templates.</p>
      )}

      <div className="mt-4 rounded-lg border border-slate-200 divide-y">
        {active.signers.map(s => (
          <div key={s.client_id} className="p-3 flex items-center justify-between gap-3">
            <span className="text-sm flex items-center gap-2">
              {s.signed ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <CircleDashed className="w-4 h-4 text-slate-300" />}
              {s.name}{s.is_me && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">You</span>}
            </span>
            <span className="text-xs text-slate-500">{s.signed ? `Signed ${new Date(s.signed_at).toLocaleString("en-GB")}` : "Awaiting signature"}</span>
          </div>
        ))}
      </div>

      {!me?.signed && me?.is_me && (
        <SignatureBlock
          key={`sig-${active.form}`}
          caseId={caseId}
          endpoint="/onboarding/cases/{id}/ccl/sign"
          declarationLabel={active.form === "tob"
            ? "I confirm I have read and accept the Terms of Business above."
            : "I confirm I have read and agree to the Client Care Letter above."}
          extraPayload={{ form: active.form }}
          testid={`wizard-ccl-signature-${active.form}`}
          refresh={refresh} />
      )}
      {active.all_signed && active.final_document_id && (
        <button type="button"
           onClick={() => downloadAuthed(`/documents/${active.final_document_id}/download`, `${active.label}.pdf`)
             .catch(() => toast.error("Could not download the document."))}
           data-testid={`wizard-ccl-download-${active.form}`}
           className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#100f5d] hover:underline">
          <FileText className="w-4 h-4" /> Download signed {active.label}
        </button>
      )}
    </Card>
  );
}

// ─────────────────────────── Step 4 · Questionnaires ─────────────────
function QuestionnairesStep({ caseId, data, refresh }) {
  const [openKey, setOpenKey] = useState(null);
  // Pause the background poll while a questionnaire is open — polling
  // re-runs the whole-wizard fetch and flickers/re-renders the page
  // under the client while they're mid-answer.
  useEffect(() => {
    if (openKey) return;
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [refresh, openKey]);
  // Only show questionnaires the solicitor has explicitly assigned to
  // the client — items that are merely "applicable to the matter type"
  // but not assigned should stay hidden from the client's wizard.
  const items = (data?.applicable || []).filter(
    x => x.status === "submitted" || x.status === "all_submitted"
      || x.status === "draft" || x.status === "assigned" || x.status === "in_progress"
      || (x.assigned_count || 0) > 0,
  );
  return (
    <Card testid="wizard-questionnaires">
      <StepTitle icon={ClipboardList} title="Your questionnaires" done={items.length > 0 && items.every(x => x.status === "all_submitted" || x.status === "submitted")} />
      {items.length === 0 ? (
        <Banner tone="info">Your solicitor hasn't sent you any questionnaires yet.</Banner>
      ) : (
        <ul className="mt-4 space-y-2">
          {items.map(x => {
            const done = x.status === "submitted" || x.status === "all_submitted";
            return (
              <li key={x.key} className={`rounded-lg border p-4 flex items-center justify-between gap-3 ${done ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`wizard-q-${x.key}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900">{x.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{x.description}</p>
                  {x.estimated_minutes && <p className="text-[11px] text-slate-400 mt-1">Approx. {x.estimated_minutes} min</p>}
                </div>
                <button onClick={() => setOpenKey(x.key)}
                  data-testid={`wizard-q-open-${x.key}`}
                  className="shrink-0 px-3 py-2 rounded-lg text-xs font-semibold bg-[#100f5d] text-white">
                  {done ? "View" : x.status === "in_progress" ? "Continue" : "Start"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {openKey && (
        <QuestionnaireForm
          open={!!openKey}
          onOpenChange={(v) => !v && setOpenKey(null)}
          caseId={caseId}
          qKey={openKey}
          onSubmitted={refresh}
        />
      )}
    </Card>
  );
}

// ─────────────────────────── Step 5 · TA form ────────────────────────
function TaStep({ caseId, data, pdfData, nativeData, refresh }) {
  // PREFERRED flow when native-schema forms apply (LEAP-style rebuild).
  // The uploaded Law-Society master PDFs have no fillable widgets, so
  // we present a proper sectioned form here and render a firm-branded
  // signed PDF at the end. Kills the "raw AcroForm codes" experience.
  if (nativeData?.enabled) {
    return <TaNativeFlow caseId={caseId} nativeData={nativeData} refresh={refresh} />;
  }
  // Legacy path — firm has uploaded fillable PDFs (currently the
  // Law-Society ones aren't fillable, so this rarely fires).
  if (pdfData?.enabled) {
    return <TaPdfFlow caseId={caseId} pdfData={pdfData} refresh={refresh} />;
  }
  if (!data) return <Card><p className="text-sm text-slate-500">Loading TA form…</p></Card>;
  if (!data.forms.length) {
    return <Card testid="wizard-ta"><StepTitle icon={FileText} title="Property information form" /><Banner tone="info">No shared property form is required for this matter.</Banner></Card>;
  }
  const me = (data.signers || []).find(s => s.is_me);
  const someoneElseFilled = !!data.forms.find(f => f.filled_by && f.filled_by.email !== me?.email);
  return (
    <Card testid="wizard-ta">
      <StepTitle icon={FileText} title="Property information form" done={data.all_signed} />
      <p className="text-sm text-slate-600 mt-2">
        This form is shared by everyone on the case — one version, agreed by all clients. If someone has already filled it in, please <strong>review each answer</strong> then sign to confirm you agree.
      </p>
      {someoneElseFilled && <Banner tone="info">
        Filled in by {data.forms.map(f => f.filled_by?.name).filter(Boolean).join(", ")}. Please review the answers before signing.
      </Banner>}

      <TaEditor caseId={caseId} forms={data.forms} refresh={refresh} />

      <div className="mt-6 rounded-lg border border-slate-200 divide-y">
        {data.signers.map(s => (
          <div key={s.client_id} className="p-3 flex items-center justify-between gap-3">
            <span className="text-sm flex items-center gap-2">
              {s.signed ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> :
               s.stale ? <AlertCircle className="w-4 h-4 text-amber-500" /> :
                         <CircleDashed className="w-4 h-4 text-slate-300" />}
              {s.name}{s.is_me && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">You</span>}
            </span>
            <span className="text-xs text-slate-500">
              {s.signed ? `Signed ${new Date(s.signed_at).toLocaleString("en-GB")}` :
               s.stale ? "Signature stale — answers were updated" : "Awaiting signature"}
            </span>
          </div>
        ))}
      </div>

      {(!me?.signed || me?.stale) && me && data.answers_complete && (
        <SignatureBlock caseId={caseId} endpoint="/onboarding/cases/{id}/ta/sign"
          declarationLabel="I have reviewed the answers above and confirm they are true and complete to the best of my knowledge." refresh={refresh} />
      )}
      {!data.answers_complete && <Banner tone="warning">Please complete every required question before signing.</Banner>}
      {data.all_signed && data.final_document_id && (
        <button type="button"
           onClick={() => downloadAuthed(`/documents/${data.final_document_id}/download`, "TA form.pdf")
             .catch(() => toast.error("Could not download the document."))}
           className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#100f5d] hover:underline">
          <FileText className="w-4 h-4" /> Download signed TA form
        </button>
      )}
    </Card>
  );
}

// ─────────────────────── Step 5 · Native TA flow ────────────────────
// LEAP-style rebuild — client fills a sectioned web form with proper
// question labels + tickable radios; on final submit the backend
// renders a firm-branded PDF that mirrors the Law-Society TA10 layout.
// The uploaded Law-Society master PDFs have no fillable widgets, so
// this native flow supersedes the legacy TaPdfFlow when applicable.
function TaNativeFlow({ caseId, nativeData, refresh }) {
  const forms = nativeData.forms || [];
  const [activeForm, setActiveForm] = useState(forms[0]?.form || null);
  const stepDone = forms.length > 0 && forms.every(f => f.all_signed);

  useEffect(() => {
    if (!activeForm && forms[0]) setActiveForm(forms[0].form);
  }, [forms, activeForm]);

  if (!forms.length) {
    return <Card testid="wizard-ta-native"><StepTitle icon={FileText} title="Property information forms" /><Banner tone="info">No native TA form applies to this matter.</Banner></Card>;
  }
  return (
    <Card testid="wizard-ta-native">
      <StepTitle icon={FileText} title="Property information forms" done={stepDone} />
      <p className="text-sm text-slate-600 mt-2">
        Please complete each section below. Every seller on the case must
        sign each form. Your answers save automatically as you go.
      </p>

      {/* Tabs */}
      <div className="mt-4 flex flex-wrap gap-2 border-b border-slate-200">
        {forms.map(f => {
          const isActive = activeForm === f.form;
          return (
            <button key={f.form} type="button" onClick={() => setActiveForm(f.form)}
              data-testid={`wizard-ta-native-tab-${f.form}`}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                isActive ? "border-[#100f5d] text-[#100f5d]"
                         : "border-transparent text-slate-500 hover:text-slate-900"
              }`}>
              {f.all_signed ? <CheckCircle2 className="w-4 h-4 inline mr-1 text-emerald-600" />
                            : <CircleDashed className="w-4 h-4 inline mr-1 text-slate-300" />}
              {f.label}
            </button>
          );
        })}
      </div>

      {activeForm && (
        <TaNativeFormEditor key={activeForm} caseId={caseId} form={activeForm} refresh={refresh} />
      )}
    </Card>
  );
}

function TaNativeFormEditor({ caseId, form, refresh }) {
  const [state, setState] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [openSection, setOpenSection] = useState(0);
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/onboarding/cases/${caseId}/ta-native/${form}`);
      setState(data);
      setValues(data.values || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the form.");
    } finally { setLoading(false); }
  }, [caseId, form]);

  useEffect(() => { load(); }, [load]);

  const scheduleSave = useCallback((next) => {
    setDirty(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await api.put(`/onboarding/cases/${caseId}/ta-native/${form}/values`, { values: next });
        setDirty(false);
        // Refresh signer states so staleness flags propagate.
        setState(prev => prev ? { ...prev, signers: data.signers, updated_at: data.updated_at } : prev);
      } catch {
        toast.error("Auto-save failed — retry your last change.");
      } finally { setSaving(false); }
    }, 900);
  }, [caseId, form]);

  const setItem = (itemKey, patch) => {
    setValues(prev => {
      const k = `item_${itemKey}`;
      const next = { ...prev, [k]: { ...(prev[k] || {}), ...patch } };
      scheduleSave(next);
      return next;
    });
  };

  const setHeader = (field, v) => {
    setValues(prev => {
      const next = { ...prev, [field]: v };
      scheduleSave(next);
      return next;
    });
  };

  if (loading || !state) return <div className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading form…</div>;

  const me = (state.signers || []).find(s => s.is_me);
  const iSigned = !!me?.signed;
  const allSigned = (state.signers || []).length > 0 && state.signers.every(s => s.signed);

  // Detect layout mode: item-grid (TA10) uses `items` w/ choice/price/notes;
  // Q&A radios (TA6/TA7) use `fields` w/ options.
  const isFieldsMode = state.sections.some(s => Array.isArray(s.fields));

  // A field is "counted" toward completion only when it's a visible
  // radio question. Textareas (free-text details) and hidden
  // conditional (show_if) fields are optional so they never block the
  // client from signing.
  const isVisible = (f) => {
    const cond = f.show_if;
    if (!cond) return true;
    const got = values[cond.key];
    if ("equals" in cond && got !== cond.equals) return false;
    if ("in" in cond && !cond.in.includes(got)) return false;
    if ("not_in" in cond && cond.not_in.includes(got ?? "")) return false;
    return true;
  };
  const countedFields = (section) =>
    (section.fields || []).filter((f) => f.type !== "textarea" && isVisible(f));

  const countAnswered = (section) => {
    if (isFieldsMode) {
      return countedFields(section).filter(f => values[f.key]).length;
    }
    return (section.items || []).filter(it => {
      const e = values[`item_${it.key}`];
      return !!e && (e.choice || e.price || (e.notes && e.notes.trim()));
    }).length;
  };
  const sizeOf = (section) => (isFieldsMode ? countedFields(section).length
                                            : (section.items || []).length);

  const totalItems = state.sections.reduce((n, s) => n + sizeOf(s), 0);
  const answered = state.sections.reduce((n, s) => n + countAnswered(s), 0);
  const complete = answered === totalItems;

  const setField = (fieldKey, v) => {
    setValues(prev => {
      const next = { ...prev, [fieldKey]: v };
      scheduleSave(next);
      return next;
    });
  };

  return (
    <div className="mt-5" data-testid={`wizard-ta-native-editor-${form}`}>
      {/* Save status */}
      <div className="flex items-center justify-between text-xs mb-3">
        <p className="text-slate-500">Progress: <b className="text-slate-800">{answered}</b> of {totalItems} items</p>
        <p className="text-slate-500 flex items-center gap-1.5">
          {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> :
           dirty  ? "Unsaved changes" :
                    <><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Saved</>}
        </p>
      </div>

      {/* Header block — property + seller (autofilled but editable) */}
      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-sm">
          <span className="block text-[11px] font-medium text-slate-500 mb-1">Property address</span>
          <textarea rows={2} className="w-full text-sm border border-slate-300 rounded px-2.5 py-1.5"
            value={values.property_address ?? ""} disabled={iSigned}
            onChange={e => setHeader("property_address", e.target.value)}
            data-testid={`ta-native-header-property_address`} />
        </label>
        <label className="text-sm">
          <span className="block text-[11px] font-medium text-slate-500 mb-1">Seller(s)</span>
          <input type="text" className="w-full text-sm border border-slate-300 rounded px-2.5 py-1.5"
            value={values.seller_names ?? ""} disabled={iSigned}
            onChange={e => setHeader("seller_names", e.target.value)}
            data-testid={`ta-native-header-seller_names`} />
        </label>
      </div>

      {/* Sections */}
      <div className="space-y-2">
        {state.sections.map((section, i) => {
          const isOpen = openSection === i;
          const sSize = sizeOf(section);
          const sAnswered = countAnswered(section);
          return (
            <div key={section.title} className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <button type="button" onClick={() => setOpenSection(isOpen ? -1 : i)}
                data-testid={`ta-native-section-${i}-toggle`}
                className={`w-full flex items-center justify-between px-4 py-3 text-left ${isOpen ? "bg-slate-50" : "hover:bg-slate-50"}`}>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{section.title}</p>
                  {section.help && <p className="text-[11px] text-slate-500 mt-0.5">{section.help}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${sAnswered === sSize ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    {sAnswered}/{sSize}
                  </span>
                  <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-slate-100 divide-y divide-slate-100">
                  {isFieldsMode
                    ? (section.fields || []).map(field => {
                        // Conditional visibility — hide `show_if` fields
                        // when the guarded value doesn't match. Currently
                        // supports `equals`, `in`, `not_in` predicates.
                        const cond = field.show_if;
                        if (cond) {
                          const got = values[cond.key];
                          if ("equals" in cond && got !== cond.equals) return null;
                          if ("in" in cond && !cond.in.includes(got)) return null;
                          if ("not_in" in cond && cond.not_in.includes(got ?? "")) return null;
                        }
                        const val = values[field.key];
                        if (field.type === "textarea") {
                          return (
                            <div key={field.key} className="p-3">
                              <p className="text-xs font-medium text-slate-500 mb-1">{field.label}</p>
                              <textarea rows={field.rows || 3}
                                value={val ?? ""} disabled={iSigned}
                                onChange={e => setField(field.key, e.target.value)}
                                className="w-full text-sm border border-slate-300 rounded px-2.5 py-1.5"
                                data-testid={`ta-native-${field.key}`} />
                            </div>
                          );
                        }
                        return (
                          <div key={field.key} className="p-3">
                            <p className="text-sm text-slate-800 mb-2">{field.label}</p>
                            <div className="flex flex-wrap gap-3">
                              {(field.options || []).map(opt => (
                                <label key={opt}
                                  className={`inline-flex items-center gap-1.5 text-sm cursor-pointer px-3 py-1 rounded-full border transition-colors ${
                                    val === opt ? "bg-[#100f5d] text-white border-[#100f5d]"
                                                : "border-slate-300 text-slate-700 hover:bg-slate-50"
                                  } ${iSigned ? "cursor-not-allowed opacity-60" : ""}`}>
                                  <input type="radio" name={`ta-${form}-${field.key}`}
                                    checked={val === opt} disabled={iSigned}
                                    onChange={() => setField(field.key, opt)}
                                    className="sr-only"
                                    data-testid={`ta-native-${field.key}-${opt.replace(/\s+/g, "_")}`} />
                                  {opt}
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })
                    : (section.items || []).map(item => {
                        const entry = values[`item_${item.key}`] || {};
                        return (
                          <div key={item.key} className="p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                            <p className="md:col-span-4 text-sm text-slate-800 pt-2">{item.label}</p>
                            <div className="md:col-span-5 flex flex-wrap gap-3">
                              {[
                                { v: "included", label: "Included" },
                                { v: "excluded", label: "Excluded" },
                                { v: "none",     label: "None" },
                              ].map(o => (
                                <label key={o.v} className={`inline-flex items-center gap-1.5 text-sm cursor-pointer ${iSigned ? "cursor-not-allowed opacity-60" : ""}`}>
                                  <input type="radio" name={`ta-${form}-${item.key}`}
                                    checked={entry.choice === o.v} disabled={iSigned}
                                    onChange={() => setItem(item.key, { choice: o.v })}
                                    data-testid={`ta-native-${item.key}-${o.v}`} />
                                  {o.label}
                                </label>
                              ))}
                            </div>
                            <input type="number" step="0.01" placeholder="£ price"
                              className="md:col-span-1 text-sm border border-slate-300 rounded px-2 py-1.5 w-full"
                              value={entry.price ?? ""} disabled={iSigned}
                              onChange={e => setItem(item.key, { price: e.target.value })}
                              data-testid={`ta-native-${item.key}-price`} />
                            <input type="text" placeholder="Notes"
                              className="md:col-span-2 text-sm border border-slate-300 rounded px-2 py-1.5 w-full"
                              value={entry.notes ?? ""} disabled={iSigned}
                              onChange={e => setItem(item.key, { notes: e.target.value })}
                              data-testid={`ta-native-${item.key}-notes`} />
                          </div>
                        );
                      })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Signers */}
      <div className="mt-6 rounded-lg border border-slate-200 divide-y">
        {(state.signers || []).map(s => (
          <div key={s.client_id} className="p-3 flex items-center justify-between gap-3">
            <span className="text-sm flex items-center gap-2">
              {s.signed ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> :
               s.stale ? <AlertCircle className="w-4 h-4 text-amber-500" /> :
                         <CircleDashed className="w-4 h-4 text-slate-300" />}
              {s.name}{s.is_me && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">You</span>}
            </span>
            <span className="text-xs text-slate-500">
              {s.signed ? `Signed ${new Date(s.signed_at).toLocaleString("en-GB")}` :
               s.stale ? "Signature stale — answers were updated" : "Awaiting signature"}
            </span>
          </div>
        ))}
      </div>

      {/* TA10 (item grid) items are optional by nature — blank means "not
          included" — so the seller can sign after review. TA6/TA7 radio
          questions must all be answered first. */}
      {!iSigned && me && isFieldsMode && !complete && (
        <Banner tone="warning">Please answer every item (Included / Excluded / None) before signing.</Banner>
      )}
      {!iSigned && me && (!isFieldsMode || complete) && (
        <TaNativeSignatureBlock caseId={caseId} form={form} values={values}
          onSigned={async () => { await load(); await refresh(); }} />
      )}
      {allSigned && state.final_document_id && (
        <button type="button"
          onClick={() => downloadAuthed(`/documents/${state.final_document_id}/download`, `${form}.pdf`)
            .catch(() => toast.error("Could not download the document."))}
          data-testid={`wizard-ta-native-download-${form}`}
          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#100f5d] hover:underline">
          <FileText className="w-4 h-4" /> Download signed {form}
        </button>
      )}
    </div>
  );
}

function TaNativeSignatureBlock({ caseId, form, values, onSigned }) {
  const [typedName, setTypedName] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  const clear = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };
  const start = (e) => {
    setDrawing(true);
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2; ctx.lineCap = "round";
    const p = "touches" in e ? e.touches[0] : e;
    ctx.beginPath(); ctx.moveTo(p.clientX - r.left, p.clientY - r.top);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    const p = "touches" in e ? e.touches[0] : e;
    ctx.lineTo(p.clientX - r.left, p.clientY - r.top);
    ctx.stroke();
    setHasInk(true);
  };
  const stop = () => setDrawing(false);

  const sign = async () => {
    if (!accept) { toast.error("Please tick the declaration."); return; }
    const c = canvasRef.current;
    if (!hasInk) { toast.error("Please draw or type your signature."); return; }
    const dataurl = c.toDataURL("image/png");
    setBusy(true);
    try {
      await api.post(`/onboarding/cases/${caseId}/ta-native/${form}/sign`, {
        values, signature_dataurl: dataurl, typed_name: typedName || null, accept_declaration: true,
      });
      toast.success(`${form} signed.`);
      await onSigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save signature.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-lg border border-slate-200 bg-white p-4" data-testid={`ta-native-sign-${form}`}>
      <p className="text-sm font-semibold text-slate-800 mb-2">Sign to confirm</p>
      <p className="text-xs text-slate-500 mb-3">
        I confirm the information in this {form} is true and complete to the best of my knowledge and belief.
      </p>
      <input type="text" className="w-full text-sm border border-slate-300 rounded px-2.5 py-1.5 mb-3"
        placeholder="Type your full name" value={typedName} onChange={e => setTypedName(e.target.value)}
        data-testid={`ta-native-typed-name-${form}`} />
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 mb-2">
        <canvas ref={canvasRef} width={500} height={140}
          className="w-full h-[140px] cursor-crosshair touch-none"
          onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={move} onTouchEnd={stop}
          data-testid={`ta-native-signature-canvas-${form}`} />
      </div>
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={clear} className="text-xs text-slate-500 hover:text-slate-800" data-testid={`ta-native-clear-${form}`}>Clear signature</button>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={accept} onChange={e => setAccept(e.target.checked)}
            data-testid={`ta-native-accept-${form}`} />
          I accept the declaration above.
        </label>
      </div>
      <button type="button" onClick={sign} disabled={busy || !accept || !hasInk}
        data-testid={`ta-native-sign-btn-${form}`}
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#100f5d] text-white disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
        Sign {form}
      </button>
    </div>
  );
}



// ─────────────────────── Step 5 · PDF fillable flow ─────────────────
// Rendered when the firm has uploaded master fillable TA PDFs.
// Uses `pdf-lib` to read the AcroForm fields, renders them as HTML
// inputs, saves partial values to the backend, then submits the final
// values + signature — backend flattens & files the signed PDF.
function TaPdfFlow({ caseId, pdfData, refresh }) {
  const me = (pdfData.signers || []).find(s => s.is_me);
  const [activeForm, setActiveForm] = useState(pdfData.forms[0]?.form || null);

  useEffect(() => {
    if (!activeForm && pdfData.forms[0]) setActiveForm(pdfData.forms[0].form);
  }, [pdfData.forms, activeForm]);

  return (
    <Card testid="wizard-ta">
      <StepTitle icon={FileText} title="Property information forms" done={pdfData.all_signed} />
      <p className="text-sm text-slate-600 mt-2">
        Please complete each form below. Every client on the case must review and sign each form. Your answers save automatically as you type.
      </p>

      {/* Form tabs */}
      <div className="mt-4 flex flex-wrap gap-2 border-b border-slate-200">
        {pdfData.forms.map(f => {
          const isDone = pdfData.signers.every(s => s.per_form?.[f.form]?.signed);
          const isMeSigned = me?.per_form?.[f.form]?.signed;
          const isActive = activeForm === f.form;
          return (
            <button key={f.form} type="button" onClick={() => setActiveForm(f.form)}
              data-testid={`wizard-ta-pdf-tab-${f.form}`}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                isActive ? "border-[#100f5d] text-[#100f5d]"
                         : "border-transparent text-slate-500 hover:text-slate-900"
              }`}>
              {isDone ? <CheckCircle2 className="w-4 h-4 inline mr-1 text-emerald-600" /> :
               isMeSigned ? <CheckCircle2 className="w-4 h-4 inline mr-1 text-amber-500" /> :
                            <CircleDashed className="w-4 h-4 inline mr-1 text-slate-300" />}
              {f.form}
            </button>
          );
        })}
      </div>

      {activeForm && (
        <TaPdfFormEditor
          key={activeForm}
          caseId={caseId}
          formMeta={pdfData.forms.find(f => f.form === activeForm)}
          signers={pdfData.signers}
          refresh={refresh}
        />
      )}
    </Card>
  );
}

function TaPdfFormEditor({ caseId, formMeta, signers, refresh }) {
  const [pdfBytes, setPdfBytes] = useState(null);    // raw bytes for the preview iframe
  const [fields, setFields] = useState([]);          // AcroForm field specs from the backend
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const saveTimer = useRef(null);
  const me = signers.find(s => s.is_me);
  const iSigned = !!me?.per_form?.[formMeta.form]?.signed;

  // Fetch both the raw PDF (for preview) AND the field list (from pypdf server-side).
  // pdf-lib in the browser chokes on hierarchical AcroForm trees in the real
  // Law Society templates, so we drive the editor from the JSON spec instead.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [pdfRes, fieldsRes] = await Promise.all([
          api.get(`/onboarding/cases/${caseId}/ta/pdf/${formMeta.form}/template`,
                  { responseType: "arraybuffer" }),
          api.get(`/onboarding/cases/${caseId}/ta/pdf/${formMeta.form}/fields`),
        ]);
        if (cancelled) return;
        setPdfBytes(new Uint8Array(pdfRes.data));
        setFields(fieldsRes.data.fields || []);
        setValues(formMeta.values || {});
      } catch (e) {
        console.error("[TaPdfFormEditor] load failed", e);
        toast.error(e.response?.data?.detail || "Could not load the form.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
     
  }, [caseId, formMeta.form]);

  // Auto-save with 900ms debounce.
  const scheduleSave = useCallback((next) => {
    setDirty(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.put(`/onboarding/cases/${caseId}/ta/pdf/${formMeta.form}/values`, { values: next });
        setDirty(false);
      } catch (e) {
        toast.error("Auto-save failed — retry your last change.");
      } finally { setSaving(false); }
    }, 900);
  }, [caseId, formMeta.form]);

  const setValue = (name, v) => {
    setValues(prev => {
      const next = { ...prev, [name]: v };
      scheduleSave(next);
      return next;
    });
  };

  // Preview URL for embedded PDF viewer (blank template).
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!pdfBytes) return;
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdfBytes]);

  if (loading) return <div className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading form…</div>;
  if (!fields.length) return <Banner tone="warning">This PDF has no fillable fields. Ask your solicitor to upload a version with AcroForm fields.</Banner>;

  return (
    <div className="mt-5" data-testid={`wizard-ta-pdf-editor-${formMeta.form}`}>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left · fillable fields */}
        <div className="lg:col-span-3 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wider text-slate-400">
              {fields.length} field{fields.length === 1 ? "" : "s"}
            </p>
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> :
               dirty  ? "Unsaved changes" :
                        <><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Saved</>}
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white max-h-[520px] overflow-y-auto divide-y divide-slate-100">
            {fields.map(f => (
              <PdfFieldInput key={f.name} field={f}
                value={values[f.name]}
                onChange={(v) => setValue(f.name, v)}
                disabled={iSigned} />
            ))}
          </div>
        </div>

        {/* Right · PDF preview */}
        <div className="lg:col-span-2">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Original PDF</p>
          {previewUrl && (
            <iframe title={`${formMeta.form}-preview`} src={previewUrl}
              className="w-full h-[520px] rounded-lg border border-slate-200 bg-white"
              data-testid={`wizard-ta-pdf-preview-${formMeta.form}`} />
          )}
        </div>
      </div>

      {/* Signers per this form */}
      <div className="mt-5 rounded-lg border border-slate-200 divide-y">
        {signers.map(s => {
          const pf = s.per_form?.[formMeta.form] || {};
          return (
            <div key={s.client_id} className="p-3 flex items-center justify-between gap-3">
              <span className="text-sm flex items-center gap-2">
                {pf.signed ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                           : <CircleDashed className="w-4 h-4 text-slate-300" />}
                {s.name}{s.is_me && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">You</span>}
              </span>
              <span className="text-xs text-slate-500">
                {pf.signed ? `Signed ${new Date(pf.signed_at).toLocaleString("en-GB")}` : "Awaiting signature"}
              </span>
            </div>
          );
        })}
      </div>

      {!iSigned && me && (
        <PdfSignatureBlock
          caseId={caseId}
          form={formMeta.form}
          values={values}
          onSigned={async () => {
            await refresh();
          }}
        />
      )}
      {formMeta.final_document_id && (
        <button type="button"
           onClick={() => downloadAuthed(`/documents/${formMeta.final_document_id}/download`, `${formMeta.form}.pdf`)
             .catch(() => toast.error("Could not download the document."))}
           className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#100f5d] hover:underline"
           data-testid={`wizard-ta-pdf-download-${formMeta.form}`}>
          <FileText className="w-4 h-4" /> Download current signed {formMeta.form}
        </button>
      )}
    </div>
  );
}

function PdfFieldInput({ field, value, onChange, disabled }) {
  const cls = "w-full text-sm border border-slate-300 rounded px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#100f5d]/20 disabled:bg-slate-50 disabled:text-slate-400";
  const label = <label className="block text-[11px] font-medium text-slate-500 mb-1 font-mono break-all">{field.name}</label>;
  const isDisabled = disabled || field.readonly;

  if (field.type === "checkbox") {
    return <div className="p-2.5">
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={value === true || value === "Yes"} disabled={isDisabled}
          onChange={e => onChange(e.target.checked)}
          data-testid={`ta-pdf-field-${field.name}`} />
        <span className="font-mono text-[11px] break-all">{field.name}</span>
      </label>
    </div>;
  }
  if (field.type === "radio" || field.type === "select") {
    const options = field.options || [];
    if (options.length > 0) {
      return <div className="p-2.5">
        {label}
        <select className={cls} value={value ?? ""} disabled={isDisabled}
          onChange={e => onChange(e.target.value)}
          data-testid={`ta-pdf-field-${field.name}`}>
          <option value="">— Select —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>;
    }
    // Fallback to text when we can't detect options.
  }
  if (field.multiline) {
    return <div className="p-2.5">
      {label}
      <textarea rows={2} className={cls} value={value ?? ""} disabled={isDisabled}
        onChange={e => onChange(e.target.value)}
        data-testid={`ta-pdf-field-${field.name}`} />
    </div>;
  }
  return <div className="p-2.5">
    {label}
    <input type="text" className={cls} value={value ?? ""} disabled={isDisabled}
      onChange={e => onChange(e.target.value)}
      data-testid={`ta-pdf-field-${field.name}`} />
  </div>;
}

function PdfSignatureBlock({ caseId, form, values, onSigned }) {
  const canvasRef = useRef(null);
  const [typedName, setTypedName] = useState("");
  const [mode, setMode] = useState("draw");
  const [font, setFont] = useState("Homemade Apple");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  const start = (e) => {
    setDrawing(true);
    const c = canvasRef.current; if (!c) return;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(getX(e, rect), getY(e, rect));
  };
  const move = (e) => {
    if (!drawing) return;
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    ctx.lineTo(getX(e, rect), getY(e, rect));
    ctx.lineWidth = 2; ctx.strokeStyle = "#0f172a"; ctx.lineCap = "round";
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => setDrawing(false);
  const clear = () => { const c = canvasRef.current; if (!c) return; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasInk(false); };
  const getX = (e, rect) => (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
  const getY = (e, rect) => (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

  const submit = async () => {
    if (!accept) { toast.error("Please tick the declaration."); return; }
    let dataurl;
    if (mode === "type") {
      if (!typedName.trim()) { toast.error("Please type your full name."); return; }
      try { await document.fonts.load(`48px "${font}"`); } catch { /* noop */ }
      const off = document.createElement("canvas"); off.width = 600; off.height = 120;
      const ctx = off.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, off.width, off.height);
      ctx.fillStyle = "#0f172a"; ctx.font = `48px "${font}", 'Brush Script MT', cursive`;
      ctx.fillText(typedName.trim(), 20, 80);
      dataurl = off.toDataURL("image/png");
    } else {
      if (!hasInk) { toast.error("Please draw your signature."); return; }
      dataurl = canvasRef.current.toDataURL("image/png");
    }
    setBusy(true);
    try {
      const r = await api.post(`/onboarding/cases/${caseId}/ta/pdf/${form}/sign`, {
        values, signature_dataurl: dataurl, typed_name: typedName || null, accept_declaration: true,
      });
      toast.success(r.data.all_signed_for_form ? `${form} — everyone has signed, thank you.` : `${form} signed. We'll ping the next signer.`);
      await onSigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save your signature.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-lg border-2 border-[#100f5d]/20 bg-[#100f5d]/[0.03] p-4"
      data-testid={`wizard-ta-pdf-signature-${form}`}>
      <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Sign this form</p>
      <div className="flex gap-2 mb-3">
        <button onClick={() => setMode("draw")} className={`px-3 py-1.5 rounded text-xs font-semibold ${mode === "draw" ? "bg-[#100f5d] text-white" : "bg-white text-slate-600 border border-slate-200"}`} data-testid={`ta-pdf-sig-mode-draw-${form}`}>Draw</button>
        <button onClick={() => setMode("type")} className={`px-3 py-1.5 rounded text-xs font-semibold ${mode === "type" ? "bg-[#100f5d] text-white" : "bg-white text-slate-600 border border-slate-200"}`} data-testid={`ta-pdf-sig-mode-type-${form}`}>Type</button>
      </div>
      {mode === "draw" ? (
        <div>
          <canvas ref={canvasRef} width={600} height={140}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            className="w-full h-32 bg-white border border-slate-300 rounded touch-none cursor-crosshair"
            data-testid={`ta-pdf-sig-canvas-${form}`} />
          <button onClick={clear} className="mt-1 text-xs text-slate-500 hover:text-slate-700">Clear</button>
        </div>
      ) : (
        <div>
          <div className="flex gap-2 mb-2">
            {["Homemade Apple", "Dancing Script"].map(f => (
              <button key={f} type="button" onClick={() => setFont(f)}
                className={`text-xs px-2 py-1 rounded ${font === f ? "bg-[#100f5d] text-white" : "bg-white text-slate-600 border border-slate-200"}`}
                style={{ fontFamily: `"${f}", cursive` }}>{f}</button>
            ))}
          </div>
          <input type="text" placeholder="Type your full name" value={typedName} onChange={e => setTypedName(e.target.value)}
            className="w-full text-3xl text-slate-900 bg-white border border-slate-300 rounded px-3 py-3"
            style={{ fontFamily: `"${font}", cursive`, lineHeight: 1.4 }}
            data-testid={`ta-pdf-sig-typed-${form}`} />
        </div>
      )}
      <label className="mt-3 flex items-start gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={accept} onChange={e => setAccept(e.target.checked)} className="mt-1"
          data-testid={`ta-pdf-sig-accept-${form}`} />
        <span>I have reviewed the answers above and confirm they are true and complete to the best of my knowledge.</span>
      </label>
      <button onClick={submit} disabled={busy}
        data-testid={`ta-pdf-sig-submit-${form}`}
        className="mt-3 px-5 py-2.5 rounded-lg text-sm font-semibold bg-[#100f5d] text-white disabled:opacity-50">
        {busy ? "Recording signature…" : `Sign ${form}`}
      </button>
      <p className="text-[11px] text-slate-400 mt-2">Your IP address and the current time will be stamped with your signature for evidence.</p>
    </div>
  );
}

function TaEditor({ caseId, forms, refresh }) {
  const [open, setOpen] = useState(forms[0]?.key || null);
  const [answers, setAnswers] = useState(() => Object.fromEntries(forms.map(f => [f.key, { ...(f.answers || {}) }])));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Reset local state if the server-side answers changed (someone else edited).
    setAnswers(Object.fromEntries(forms.map(f => [f.key, { ...(f.answers || {}) }])));
    setDirty(false);
  }, [forms]);

  const setValue = (key, fieldKey, v) => {
    setAnswers(a => ({ ...a, [key]: { ...a[key], [fieldKey]: v } }));
    setDirty(true);
  };
  const save = async (key) => {
    setSaving(true);
    try {
      await api.put(`/onboarding/cases/${caseId}/ta/answers`, { key, answers: answers[key] });
      toast.success("Answers saved");
      setDirty(false);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save answers"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-4 space-y-3">
      {forms.map(form => (
        <div key={form.key} className="rounded-lg border border-slate-200 bg-white">
          <button type="button" onClick={() => setOpen(open === form.key ? null : form.key)}
            className="w-full flex items-center justify-between px-4 py-3 text-left"
            data-testid={`wizard-ta-form-${form.key}`}>
            <span className="font-semibold text-slate-900">{form.label}</span>
            <span className="text-xs text-slate-500">
              {form.missing_required?.length ? `${form.missing_required.length} required left` : "Complete"}
            </span>
          </button>
          {open === form.key && (
            <div className="border-t border-slate-100 p-4 space-y-6">
              {form.sections.map(sec => (
                <div key={sec.title}>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">{sec.title}</h4>
                  <div className="space-y-3">
                    {sec.fields.map(f => (
                      <FieldInput key={f.key} field={f} value={answers[form.key]?.[f.key]}
                        onChange={v => setValue(form.key, f.key, v)}
                        formAnswers={answers[form.key] || {}} />
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <button onClick={() => save(form.key)} disabled={!dirty || saving}
                  data-testid={`wizard-ta-save-${form.key}`}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#100f5d] text-white disabled:opacity-50">
                  {saving ? "Saving…" : dirty ? "Save answers" : "Saved"}
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function FieldInput({ field, value, onChange, formAnswers }) {
  // Honour show_if — only render if the parent gate matches.
  if (field.show_if) {
    if (formAnswers[field.show_if.key] !== field.show_if.equals) return null;
  }
  const label = <label className="block text-sm font-medium text-slate-700 mb-1">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</label>;
  const cls = "w-full text-sm border border-slate-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#100f5d]/20";
  if (field.type === "textarea") return <div>{label}<textarea rows={3} className={cls} value={value ?? ""} onChange={e => onChange(e.target.value)} data-testid={`ta-field-${field.key}`} /></div>;
  if (field.type === "select") return <div>{label}<select className={cls} value={value ?? ""} onChange={e => onChange(e.target.value)} data-testid={`ta-field-${field.key}`}><option value="">— Select —</option>{field.options.map(o => <option key={o} value={o}>{o}</option>)}</select></div>;
  if (field.type === "radio") return <div>{label}<div className="flex gap-4">{field.options.map(o => <label key={o} className="flex items-center gap-1.5 text-sm"><input type="radio" name={field.key} checked={value === o} onChange={() => onChange(o)} data-testid={`ta-field-${field.key}-${o}`} />{o}</label>)}</div></div>;
  if (field.type === "checkbox") return <div><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={value === true} onChange={e => onChange(e.target.checked)} data-testid={`ta-field-${field.key}`} /> {field.label}</label></div>;
  if (field.type === "multiselect") return <div>{label}<div className="grid grid-cols-2 gap-1.5">{field.options.map(o => { const arr = Array.isArray(value) ? value : []; const checked = arr.includes(o); return <label key={o} className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={checked} onChange={() => onChange(checked ? arr.filter(x => x !== o) : [...arr, o])} data-testid={`ta-field-${field.key}-${o}`} />{o}</label>; })}</div></div>;
  if (field.type === "address") {
    const v = value || {};
    return <div>{label}<div className="grid grid-cols-2 gap-2"><input placeholder="Line 1" className={cls} value={v.line1 || ""} onChange={e => onChange({ ...v, line1: e.target.value })} /><input placeholder="Line 2" className={cls} value={v.line2 || ""} onChange={e => onChange({ ...v, line2: e.target.value })} /><input placeholder="Town" className={cls} value={v.town || ""} onChange={e => onChange({ ...v, town: e.target.value })} /><input placeholder="Postcode" className={cls} value={v.postcode || ""} onChange={e => onChange({ ...v, postcode: e.target.value })} /></div></div>;
  }
  const inputType = field.type === "email" ? "email" : field.type === "date" ? "date" : (field.type === "number" || field.type === "currency") ? "number" : "text";
  return <div>{label}<input type={inputType} className={cls} value={value ?? ""} onChange={e => onChange(inputType === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} data-testid={`ta-field-${field.key}`} /></div>;
}

// ─────────────────────────── Signature block ─────────────────────────
function SignatureBlock({ caseId, endpoint, declarationLabel, refresh, extraPayload, testid = "wizard-signature-block" }) {
  const canvasRef = useRef(null);
  const [typedName, setTypedName] = useState("");
  const [mode, setMode] = useState("draw"); // draw | type
  const [font, setFont] = useState("Homemade Apple"); // handwriting webfont for typed sigs
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  const start = (e) => {
    setDrawing(true);
    const c = canvasRef.current; if (!c) return;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(getX(e, rect), getY(e, rect));
  };
  const move = (e) => {
    if (!drawing) return;
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    ctx.lineTo(getX(e, rect), getY(e, rect));
    ctx.lineWidth = 2; ctx.strokeStyle = "#0f172a"; ctx.lineCap = "round";
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => setDrawing(false);
  const clear = () => { const c = canvasRef.current; if (!c) return; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasInk(false); };

  const getX = (e, rect) => (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
  const getY = (e, rect) => (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

  const submit = async () => {
    if (!accept) { toast.error("Please tick the declaration."); return; }
    let dataurl;
    if (mode === "type") {
      if (!typedName.trim()) { toast.error("Please type your full name."); return; }
      // Render typed name onto a small canvas → PNG data URL. Wait for
      // the chosen webfont to be ready so the PNG doesn't fall back
      // to a serif face on slow connections.
      try { await document.fonts.load(`48px "${font}"`); } catch { /* noop */ }
      const off = document.createElement("canvas"); off.width = 600; off.height = 120;
      const ctx = off.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, off.width, off.height);
      ctx.fillStyle = "#0f172a"; ctx.font = `48px "${font}", 'Brush Script MT', cursive`;
      ctx.fillText(typedName.trim(), 20, 80);
      dataurl = off.toDataURL("image/png");
    } else {
      if (!hasInk) { toast.error("Please draw your signature."); return; }
      dataurl = canvasRef.current.toDataURL("image/png");
    }
    setBusy(true);
    try {
      const url = endpoint.replace("{id}", caseId);
      const body = { signature_dataurl: dataurl, typed_name: typedName || null, ...(extraPayload || {}) };
      if (url.endsWith("/ccl/sign")) body.accept_terms = true;
      if (url.endsWith("/ta/sign")) body.accept_declaration = true;
      const r = await api.post(url, body);
      toast.success(r.data.all_signed ? "Everyone has signed — thank you." : "Signed. We'll ping the next signer.");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save your signature.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-lg border-2 border-[#100f5d]/20 bg-[#100f5d]/[0.03] p-4" data-testid={testid}>
      <div className="flex gap-2 mb-3">
        <button onClick={() => setMode("draw")} className={`px-3 py-1.5 rounded text-xs font-semibold ${mode === "draw" ? "bg-[#100f5d] text-white" : "bg-white text-slate-600 border border-slate-200"}`} data-testid="sig-mode-draw">Draw</button>
        <button onClick={() => setMode("type")} className={`px-3 py-1.5 rounded text-xs font-semibold ${mode === "type" ? "bg-[#100f5d] text-white" : "bg-white text-slate-600 border border-slate-200"}`} data-testid="sig-mode-type">Type</button>
      </div>
      {mode === "draw" ? (
        <div>
          <canvas ref={canvasRef} width={600} height={140}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            className="w-full h-32 bg-white border border-slate-300 rounded touch-none cursor-crosshair"
            data-testid="sig-canvas" />
          <button onClick={clear} className="mt-1 text-xs text-slate-500 hover:text-slate-700" data-testid="sig-clear">Clear</button>
        </div>
      ) : (
        <div>
          <div className="flex gap-2 mb-2">
            {["Homemade Apple", "Dancing Script"].map(f => (
              <button key={f} type="button" onClick={() => setFont(f)}
                className={`text-xs px-2 py-1 rounded ${font === f ? "bg-[#100f5d] text-white" : "bg-white text-slate-600 border border-slate-200"}`}
                style={{ fontFamily: `"${f}", cursive` }}
                data-testid={`sig-font-${f.replace(/\s+/g,'-').toLowerCase()}`}>
                {f}
              </button>
            ))}
          </div>
          <input type="text" placeholder="Type your full name" value={typedName} onChange={e => setTypedName(e.target.value)}
            className="w-full text-3xl text-slate-900 bg-white border border-slate-300 rounded px-3 py-3"
            style={{ fontFamily: `"${font}", cursive`, lineHeight: 1.4 }}
            data-testid="sig-typed-input" />
        </div>
      )}
      <label className="mt-3 flex items-start gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={accept} onChange={e => setAccept(e.target.checked)} className="mt-1" data-testid="sig-accept" />
        <span>{declarationLabel}</span>
      </label>
      <button onClick={submit} disabled={busy} data-testid="sig-submit"
        className="mt-3 px-5 py-2.5 rounded-lg text-sm font-semibold bg-[#100f5d] text-white disabled:opacity-50">
        {busy ? "Recording signature…" : "Sign"}
      </button>
      <p className="text-[11px] text-slate-400 mt-2">Your IP address and the current time will be stamped with your signature for evidence.</p>
    </div>
  );
}

// ─────────────────────────── Tiny helpers ────────────────────────────
function Card({ children, testid }) {
  return <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid={testid}>{children}</div>;
}
function StepTitle({ icon: Icon, title, done }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${done ? "bg-emerald-100 text-emerald-700" : "bg-[#100f5d]/10 text-[#100f5d]"}`}>
        {done ? <CheckCircle2 className="w-6 h-6" /> : <Icon className="w-5 h-5" />}
      </div>
      <div className="min-w-0"><h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h2>{done && <p className="text-xs text-emerald-600 font-medium">Completed</p>}</div>
    </div>
  );
}
function StatusPill({ status }) {
  const map = {
    approved: { c: "bg-emerald-100 text-emerald-700", t: "Approved" },
    pending:  { c: "bg-amber-100 text-amber-700",     t: "In progress" },
    in_review:{ c: "bg-blue-100 text-blue-700",       t: "In review" },
    declined: { c: "bg-red-100 text-red-700",         t: "Declined" },
    not_started: { c: "bg-slate-100 text-slate-500",  t: "Not started" },
  };
  const m = map[status] || map.not_started;
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${m.c}`}>{m.t}</span>;
}
function Banner({ children, tone = "info", ...rest }) {
  const c = { info: "bg-blue-50 text-blue-800 border-blue-100",
              success: "bg-emerald-50 text-emerald-800 border-emerald-100",
              warning: "bg-amber-50 text-amber-800 border-amber-100" }[tone];
  return <div className={`mt-4 border rounded-lg px-4 py-3 text-sm ${c}`} {...rest}>{children}</div>;
}
